import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isViewer } from "@/lib/guards";
import AppHeader from "@/components/AppHeader";
import AvatarCardProfile from "@/components/avatar/AvatarCardProfile";
import AvatarCardStrategy from "@/components/avatar/AvatarCardStrategy";
import WriteCopySheet from "@/components/avatar/WriteCopySheet";
import { CardGridSkeleton, ErrorState } from "@/components/shared/StateViews";
import DraftResumeSheet from "@/components/shared/DraftResumeSheet";
import AppSplash, { shouldShowSplash } from "@/components/AppSplash";
import MinhaSemana from "@/components/calendar/MinhaSemana";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { startAvatarCreation, discardDraft } from "@/lib/avatarFlow";
import { friendlyError } from "@/lib/errors";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { contentAgents } from "@/lib/agents";

export default function Dashboard() {
  const { user } = useAuth();
  const viewer = isViewer(user);
  const nav = useNavigate();
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({ conversas: 0, conteudos: 0, agentes_usados: 0 });
  const [avatars, setAvatars] = useState([]);
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [activeId, setActiveId] = useState(null); // local swap override
  const [swapOpen, setSwapOpen] = useState(false);
  const [writeCopyFor, setWriteCopyFor] = useState(null);
  const [lockedInfo, setLockedInfo] = useState(null); // agente bloqueado por plano
  const [generatingId, setGeneratingId] = useState(null);
  const [splash] = useState(() => shouldShowSplash());

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, s, av, p] = await Promise.all([
        api.get("/agents"),
        api.get("/artifacts/stats/overview"),
        api.get("/avatars", { params: { status: "complete" } }),
        api.get("/persona/me"),
      ]);
      setAgents(a.data);
      setStats(s.data);
      setPersona(p.data);
      setAvatars(av.data || []);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const avatarActive = useMemo(() => {
    if (!avatars.length) return null;
    if (activeId) return avatars.find((a) => a.id === activeId) || avatars[0];
    const def = user?.default_avatar_id && avatars.find((a) => a.id === user.default_avatar_id);
    const fav = avatars.find((a) => a.favorito);
    return def || fav || avatars[0];
  }, [avatars, activeId, user]);

  const createAvatar = async () => {
    try {
      await startAvatarCreation({ nav, onDraftExists: (id) => setDraftId(id) });
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const generatePhoto = async (avatar) => {
    setGeneratingId(avatar.id);
    try {
      const { data } = await api.post(`/avatars/${avatar.id}/generate-photo`);
      setAvatars((list) => list.map((a) => (a.id === avatar.id ? { ...a, foto_url: data.foto_url } : a)));
      toast.success("Retrato gerado ✦");
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setGeneratingId(null);
    }
  };

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Bom dia";
    if (h < 18) return "Boa tarde";
    return "Boa noite";
  })();

  const visibleAgents = useMemo(() => contentAgents(agents), [agents]);

  const setupProgress = (() => {
    const total = 2;
    let done = 0;
    if (persona && (persona.bandeira || persona.proposito || persona.tom_de_voz)) done += 1;
    if (avatarActive) done += 1;
    return { done, total, pct: Math.round((done / total) * 100) };
  })();

  return (
    <div className="min-h-screen bg-brand-bg starfield relative overflow-x-clip" data-testid="dashboard-page">
      {splash && <AppSplash ready={!loading} />}
      <AppHeader />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 pb-24 md:pb-10">
        <header className="mb-10">
          <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-2">
            Mission Control
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-brand-ink mb-1">
            {greeting}, {user?.name?.split(" ")[0] || "—"} ✦
          </h1>
          <p className="text-brand-ink2 text-[15px]">
            Sua plataforma de conteúdo estratégico para depilação a laser.
          </p>
        </header>

        {loading ? (
          <CardGridSkeleton count={6} />
        ) : error ? (
          <ErrorState message={error} onRetry={loadAll} testid="dashboard-error" />
        ) : (
          <>
            {/* Setup card — hidden while browsing a shared workspace read-only */}
            {!viewer && setupProgress.pct < 100 && (
              <div
                data-testid="setup-card"
                className="mb-8 bg-brand-surface border border-brand-line rounded-3xl p-7 flex flex-col sm:flex-row items-start sm:items-center gap-6"
              >
                <SetupRing pct={setupProgress.pct} />
                <div className="flex-1">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-brand-rose font-ui mb-1">
                    Setup {setupProgress.pct}%
                  </div>
                  <h3 className="font-display text-2xl text-brand-ink mb-1">
                    Termine de configurar sua marca
                  </h3>
                  <p className="text-[13.5px] text-brand-ink2">
                    Você concluiu {setupProgress.done} de {setupProgress.total} passos.
                  </p>
                </div>
                <div className="flex gap-2">
                  {!persona?.bandeira && (
                    <Link
                      data-testid="setup-cta-persona"
                      to="/onboarding/brand-persona"
                      className="px-5 py-2.5 rounded-full bg-brand-rose text-white text-[13px] font-ui hover:bg-brand-roseHover transition-colors min-h-[44px] inline-flex items-center"
                    >
                      Brand Persona
                    </Link>
                  )}
                  {!avatarActive && (
                    <button
                      data-testid="setup-cta-avatar"
                      onClick={createAvatar}
                      className="px-5 py-2.5 rounded-full bg-brand-rose text-white text-[13px] font-ui hover:bg-brand-roseHover transition-colors min-h-[44px] inline-flex items-center"
                    >
                      Avatar
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Desktop (>=lg): avatar em destaque à esquerda (sticky) ocupando toda a
                altura; à direita, na ordem, stats → Minha Semana → agentes.
                Mobile (<lg): Minha Semana sobe para o topo (antes do avatar), depois
                avatar → stats → agentes — via `order-*`. */}
            <div className="flex flex-col lg:grid lg:grid-cols-[360px_1fr] lg:gap-8 lg:items-start">
              {/* Avatar em destaque */}
              <div
                className="order-2 lg:order-none lg:col-start-1 lg:row-start-1 lg:row-span-3 lg:sticky lg:top-24 mb-10 lg:mb-0"
                data-testid="avatar-primary-card"
              >
                {avatarActive ? (
                  <AvatarCardProfile
                    avatar={avatarActive}
                    peerAvatars={avatars}
                    onUseAvatar={setWriteCopyFor}
                    onSwap={avatars.length > 1 ? () => setSwapOpen(true) : undefined}
                    onGeneratePhoto={generatePhoto}
                    generating={generatingId === avatarActive.id}
                    readOnly={viewer}
                  />
                ) : (
                  <div className="bg-brand-surface border border-brand-line rounded-2xl p-7">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-brand-ink2 font-ui mb-2">
                      Avatar em destaque
                    </div>
                    <h3 className="font-display text-2xl text-brand-ink mb-2">
                      Você ainda não tem um avatar
                    </h3>
                    <p className="text-[14px] text-brand-ink2 mb-5">
                      {viewer ? "Esta conta ainda não tem um avatar." : "Crie sua cliente ideal por entrevista guiada."}
                    </p>
                    {!viewer && (
                      <button
                        onClick={createAvatar}
                        data-testid="create-avatar-cta"
                        className="inline-flex items-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-5 py-2.5 rounded-full text-[13px] font-ui transition-colors min-h-[44px]"
                      >
                        <Plus className="w-4 h-4" /> Criar avatar
                      </button>
                    )}
                  </div>
                )}
                <div className="mt-3 flex justify-center">
                  <Link
                    data-testid="manage-avatars"
                    to="/avatars"
                    className="text-[12.5px] font-ui text-brand-ink2 hover:text-brand-rose transition-colors"
                  >
                    Gerenciar avatares →
                  </Link>
                </div>
              </div>

              {/* Stats */}
              <div className="order-3 lg:order-none lg:col-start-2 lg:row-start-1 grid grid-cols-3 gap-4 h-fit mb-10">
                <Stat label="Conversas" value={stats.conversas} testid="stat-conversas" />
                <Stat label="Conteúdos" value={stats.conteudos} testid="stat-conteudos" />
                <Stat label="Agentes" value={stats.agentes_usados} testid="stat-agentes" />
              </div>

              {/* Minha Semana — topo no mobile, entre stats e agentes no desktop */}
              <div className="order-1 lg:order-none lg:col-start-2 lg:row-start-2">
                <MinhaSemana viewer={viewer} />
              </div>

              {/* Seus agentes */}
              <div className="order-4 lg:order-none lg:col-start-2 lg:row-start-3">
                <div className="mb-5 flex items-baseline justify-between">
                  <h2 className="font-display text-3xl text-brand-ink">Seus agentes</h2>
                  <span className="text-[12px] text-brand-ink2 font-ui">
                    {visibleAgents.filter(a => a.status === "ready").length} prontos
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="agents-grid">
                  <BrandPersonaCard />
                  {visibleAgents.map((a) => (
                    <AgentCard key={a.slug} agent={a} onClick={() => onAgentClick(a, nav, createAvatar, setLockedInfo)} />
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Swap default avatar (>1) */}
      <Sheet open={!!lockedInfo} onOpenChange={(o) => !o && setLockedInfo(null)}>
        <SheetContent
          side="bottom"
          data-testid="locked-plan-sheet"
          className="bg-brand-surface border-brand-line rounded-t-3xl"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink flex items-center gap-2">
              <Lock className="w-5 h-5 text-brand-gold" />
              {lockedInfo ? (TIER_LABEL[lockedInfo.tier] || "Plano Pro") : ""}
            </SheetTitle>
            <SheetDescription className="text-brand-ink2">
              {lockedInfo?.nome} faz parte do {lockedInfo ? (TIER_LABEL[lockedInfo.tier] || "Plano Pro") : ""}.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {agents.filter((x) => x.tier === lockedInfo?.tier).map((x) => (
              <div key={x.slug} className="flex items-center gap-3 text-[14px] text-brand-ink">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-gold" />
                <span className="font-medium">{x.nome}</span>
                <span className="text-brand-ink3 text-[12.5px] truncate">{x.descricao}</span>
              </div>
            ))}
          </div>
          <p className="mt-5 text-[13px] text-brand-ink2">
            Os planos ainda não estão à venda. Fale com quem administra a sua conta para liberar.
          </p>
        </SheetContent>
      </Sheet>

      <Sheet open={swapOpen} onOpenChange={setSwapOpen}>
        <SheetContent side="bottom" data-testid="avatar-swap-sheet" className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[80vh] overflow-y-auto overflow-x-hidden scrollbar-soft">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink">Trocar avatar em destaque</SheetTitle>
            <SheetDescription className="text-brand-ink2">Escolha qual cliente ideal aparece no painel.</SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-2.5">
            {avatars.map((a) => (
              <AvatarCardStrategy
                key={a.id}
                avatar={a}
                peerAvatars={avatars}
                compact
                selected={avatarActive?.id === a.id}
                onSelect={() => { setActiveId(a.id); setSwapOpen(false); }}
              />
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <WriteCopySheet
        avatar={writeCopyFor}
        peerAvatars={avatars}
        open={Boolean(writeCopyFor)}
        onOpenChange={(o) => !o && setWriteCopyFor(null)}
      />

      <DraftResumeSheet
        draftId={draftId}
        open={Boolean(draftId)}
        onOpenChange={(o) => !o && setDraftId(null)}
        onContinue={() => nav(`/avatars/${draftId}/builder?mode=voice`)}
        onDiscard={async () => {
          try {
            await discardDraft();
            setDraftId(null);
            await startAvatarCreation({ nav });
          } catch (err) {
            toast.error(friendlyError(err));
          }
        }}
      />
    </div>
  );
}

function onAgentClick(a, nav, createAvatar, setLockedInfo) {
  if (a.status === "needs_persona") {
    nav("/onboarding/brand-persona");
  } else if (a.status === "needs_avatar") {
    createAvatar();
  } else if (a.status === "locked_plan") {
    setLockedInfo(a);
  } else if (a.status === "ready") {
    nav(`/agent/${a.slug}`);
  }
}

const TIER_LABEL = { pro: "Plano Pro", premium: "Plano Premium" };

function BrandPersonaCard() {
  return (
    <Link
      to="/marca"
      data-testid="agent-card-brand-persona"
      className="text-left bg-brand-surface border border-brand-line rounded-2xl p-6 transition-all group hover:border-brand-rose hover:shadow-[0_20px_40px_-25px_rgba(161,107,247,0.35)]"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-xl bg-brand-elevated flex items-center justify-center group-hover:bg-brand-rose group-hover:text-white text-brand-rose transition-colors">
          <LucideIcons.Sparkles className="w-5 h-5" />
        </div>
        <span className="px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase font-ui bg-brand-sage/15 text-brand-sage">
          Sua marca
        </span>
      </div>
      <h3 className="font-display text-xl text-brand-ink mb-1.5">Minha BrandPersona</h3>
      <p className="text-[13.5px] text-brand-ink2 leading-snug mb-4 min-h-[40px]">
        Linguagem, tom e posicionamento da clínica.
      </p>
      <div className="text-[12px] font-ui inline-flex items-center gap-1 group-hover:gap-2 transition-all text-brand-rose">
        Abrir <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}

function AgentCard({ agent, onClick }) {
  const Icon = LucideIcons[agent.icone] || LucideIcons.Sparkles;
  const statusStyles = {
    ready: { label: "Pronto", bg: "bg-brand-sage/15", text: "text-brand-sage" },
    needs_persona: { label: "Precisa de Persona", bg: "bg-brand-elevated", text: "text-brand-gold" },
    needs_avatar: { label: "Precisa de Avatar", bg: "bg-brand-elevated", text: "text-brand-gold" },
    locked_plan: {
      label: TIER_LABEL[agent.tier] || "Plano Pro",
      bg: "bg-brand-gold/15",
      text: "text-brand-gold",
    },
  };
  const s = statusStyles[agent.status] || statusStyles.ready;
  const locked = agent.status === "locked_plan";
  return (
    <button
      data-testid={`agent-card-${agent.slug}`}
      onClick={onClick}
      className={`text-left bg-brand-surface border border-brand-line rounded-2xl p-6 transition-all group ${
        locked
          ? "opacity-75 hover:border-brand-gold/60"
          : "hover:border-brand-rose hover:shadow-[0_20px_40px_-25px_rgba(161,107,247,0.35)]"
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-xl bg-brand-elevated flex items-center justify-center group-hover:bg-brand-rose group-hover:text-white text-brand-rose transition-colors">
          {locked ? <Lock className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <span
          className={`px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase font-ui ${s.bg} ${s.text}`}
          data-testid={`agent-status-${agent.slug}`}
        >
          {s.label}
        </span>
      </div>
      <h3 className="font-display text-xl text-brand-ink mb-1.5">{agent.nome}</h3>
      <p className="text-[13.5px] text-brand-ink2 leading-snug mb-4 min-h-[40px]">
        {agent.descricao}
      </p>
      <div
        className={`text-[12px] font-ui inline-flex items-center gap-1 group-hover:gap-2 transition-all ${
          locked ? "text-brand-gold" : "text-brand-rose"
        }`}
      >
        {locked ? "Ver o que destrava" : "Abrir conversa"} <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </button>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-brand-surface border border-brand-line rounded-2xl p-5" data-testid={testid}>
      <div className="text-[10.5px] tracking-[0.22em] uppercase text-brand-ink2 font-ui mb-2">
        {label}
      </div>
      <div className="font-display text-4xl text-brand-ink">{value}</div>
    </div>
  );
}

function SetupRing({ pct }) {
  const r = 30;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div className="relative w-20 h-20">
      <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
        <circle cx="40" cy="40" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
        <circle
          cx="40" cy="40" r={r} stroke="#A16BF7" strokeWidth="6" fill="none"
          strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-ui text-brand-ink font-medium">
        {pct}%
      </div>
    </div>
  );
}
